import React, { useState, useEffect } from 'react';
import Styles from '../../assets/css/components/logistic/logisticAdd.module.scss';
import { useNavigate } from 'react-router-dom';
import { createLogistic, warehouseList } from '../../services/Api';
import OpenEye from '../../assets/images/Auth/visibility.png';
import CloseEye from '../../assets/images/Auth/visibility_off.png';
import { useDispatch } from 'react-redux';
import { popUpBoxTrigger } from "../../features/message/popUpSlice";
import PopUpMessage from "../../utils/errors/PopUpMessage";
import { setShouldRefresh } from '../../features/logistic/logisticSlice';
import { AiOutlineLoading } from 'react-icons/ai';

const LogisticAdd = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [warehouses, setWarehouses] = useState([]);
    const [newPasswordVisible, setNewPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [formData, setFormData] = useState({
        warehouse_id: '',
        name: '',
        email: '',
        phone_number: '',
        password: '',
        password_confirmation: '',
        status: ''
    });
    const [errors, setErrors] = useState({});

    const validationRules = {
        name: { required: true, message: 'Please enter a logistic name' },
        email: { required: true, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Please enter a valid email address' },
        phone_number: { required: true, pattern: /^\d{10}$/, message: 'Please enter a valid 10-digit phone number' },
        warehouse_id: { required: true, message: 'Please select a warehouse' },
        password: { required: true, pattern: /^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/, message: 'Password must contain at least 8 characters, one uppercase letter, one number, and one special character' },
        password_confirmation: { required: true, match: 'password', message: 'Confirm password does not match' },
        status: { required: true, message: 'Please select a status' }
    };

    useEffect(() => {
        const fetchWarehouses = async () => {
            try {
                const response = await warehouseList();
                setWarehouses(response.data);
            } catch (error) {
                console.error("Error fetching warehouses:", error);
                showPopup('error', 'Error', 'Failed to load warehouses');
            }
        };
        fetchWarehouses();
    }, []);

    const showPopup = (type, heading, message) => {
        dispatch(popUpBoxTrigger({ type, heading, message, show: true }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: name === 'phone_number' ? value.replace(/[^0-9]/g, '') : value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        Object.keys(validationRules).forEach(field => {
            const rule = validationRules[field];
            const value = formData[field];

            if (rule.required && !value) {
                newErrors[field] = rule.message;
                isValid = false;
            } else if (rule.pattern && value && !rule.pattern.test(value)) {
                newErrors[field] = rule.message;
                isValid = false;
            } else if (rule.match && value !== formData[rule.match]) {
                newErrors[field] = rule.message;
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            showPopup('error', 'Validation Error', 'Please fill all required fields correctly');
            return;
        }

        try {
            setLoading(true);
            const apiData = { ...formData };
            const response = await createLogistic(apiData);

            if (response.status === 201) {
                dispatch(setShouldRefresh(true));
                showPopup('success', 'Success', 'Logistic staff created successfully');
                navigate("/logistic");
            } else {
                throw new Error('Unexpected response from the server');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to create logistic staff';
            showPopup('error', 'Error', errorMessage);
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            }
        } finally {
            setLoading(false);
        }
    };

    const hasError = (fieldName) => {
        return errors[fieldName] ? Styles.error : '';
    };

    return (
        <div className={`${Styles.logistic_add}`}>
            <PopUpMessage />
            <div className={`${Styles.heading}`}>
                <h2>Create Logistic</h2>
            </div>

            <div className={`${Styles.logistic_form}`}>
                <div className={`${Styles.logistic_details}`}>
                    <div className={`${Styles.title}`}>
                        <h4>Logistic details</h4>
                        <hr />
                    </div>
                    <div className={`${Styles.form}`}>
                        <div className={`${Styles.label_input} ${hasError('name')}`}>
                            <label>Name<span>*</span></label>
                            <div className={`${Styles.select_field}`}>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Enter Logistic Staff Name"
                                />
                                {errors.name && <span className={Styles.errorMessage}>{errors.name}</span>}
                            </div>
                        </div>

                        <div className={`${Styles.label_input} ${hasError('email')}`}>
                            <label>Email<span>*</span></label>
                            <div className={`${Styles.select_field}`}>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Enter Email"
                                />
                                {errors.email && <span className={Styles.errorMessage}>{errors.email}</span>}
                            </div>
                        </div>

                        <div className={`${Styles.label_input} ${hasError('phone_number')}`}>
                            <label>Phone number<span>*</span></label>
                            <div className={`${Styles.select_field}`}>
                                <input
                                    type="text"
                                    name="phone_number"
                                    value={formData.phone_number}
                                    onChange={handleInputChange}
                                    placeholder="Enter Phone Number"
                                />
                                {errors.phone_number && <span className={Styles.errorMessage}>{errors.phone_number}</span>}
                            </div>
                        </div>

                        <div className={`${Styles.label_input} ${hasError('status')}`}>
                            <label>Status<span>*</span></label>
                            <div className={`${Styles.select_field}`}>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    style={{ color: formData.status ? '#000000' : '#797775' }}
                                >
                                    <option value="" hidden>Select Status</option>
                                    <option value="1">Active</option>
                                    <option value="0">Inactive</option>
                                </select>
                                {errors.status && <span className={Styles.errorMessage}>{errors.status}</span>}
                            </div>
                        </div>

                        <div className={`${Styles.label_input} ${hasError('warehouse_id')}`}>
                            <label>Warehouse Name<span>*</span></label>
                            <div className={`${Styles.select_field}`}>
                                <select
                                    name="warehouse_id"
                                    value={formData.warehouse_id || ''}
                                    onChange={handleInputChange}
                                    style={{ color: formData.warehouse_id ? '#000000' : '#797775' }}
                                >
                                    <option value="" hidden>Select Warehouse Name</option>
                                    {warehouses && warehouses.filter(warehouse => warehouse.status === 1).map((warehouse) => (
                                        <option key={warehouse.id} value={warehouse.id}>
                                            {`${warehouse.name}`}
                                        </option>
                                    ))}
                                </select>
                                {errors.warehouse_id && <span className={Styles.errorMessage}>{errors.warehouse_id}</span>}
                            </div>
                        </div>

                        <div className={`${Styles.label_input} ${hasError('password')}`}>
                            <label>Password<span>*</span></label>
                            <div className={`${Styles.password_field}`}>
                                <input
                                    name="password"
                                    type={newPasswordVisible ? "text" : "password"}
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    placeholder="Enter password"
                                />
                                <img
                                    src={newPasswordVisible ? OpenEye : CloseEye}
                                    onClick={() => setNewPasswordVisible(!newPasswordVisible)}
                                    alt="Toggle visibility"
                                    className={`${Styles.eye_icon}`}
                                />
                                {errors.password && <span className={`${Styles.error_text}`}>{errors.password}</span>}
                            </div>
                        </div>

                        <div className={`${Styles.label_input} ${hasError('password_confirmation')}`}>
                            <label>Confirm Password<span>*</span></label>
                            <div className={`${Styles.password_field}`}>
                                <input
                                    name="password_confirmation"
                                    type={confirmPasswordVisible ? "text" : "password"}
                                    value={formData.password_confirmation}
                                    onChange={handleInputChange}
                                    placeholder="Confirm password"
                                />
                                <img
                                    src={confirmPasswordVisible ? OpenEye : CloseEye}
                                    onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                                    alt="Toggle visibility"
                                    className={`${Styles.eye_icon}`}
                                />
                                {errors.password_confirmation && <span className={`${Styles.error_text}`}>{errors.password_confirmation}</span>}
                            </div>
                        </div>
                    </div>

                    <div className={`${Styles.buttons}`}>
                        <button onClick={handleSubmit} disabled={loading}>{loading ? <AiOutlineLoading /> : 'Create'}</button>
                        <button onClick={() => navigate("/logistic")}>Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default LogisticAdd
